import React from 'react';
import { motion } from 'framer-motion';
import { scrollToCheckout } from '../utils/scroll';
import { ChevronRight, Brain, Zap, Shield, Lock, Download, MapPin } from 'lucide-react';
export const HeroSection = () => {
  return <section className="pt-6 sm:pt-16 pb-12 sm:pb-20 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto text-center" data-id="element-173">
      {/* Title */}
      <motion.h1 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6
    }} className="text-[28px] sm:text-5xl md:text-6xl font-bold text-dark leading-tight mb-3 sm:mb-6 font-['DM_Sans',_sans-serif]" data-id="element-174">
        Tu cabeza no para,
        <br className="hidden sm:block" data-id="element-175" />
        tu lista no se achica
        <br className="hidden sm:block" data-id="element-176" /> y vos seguís sin poder arrancar.
      </motion.h1>

      {/* Subtitle */}
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6,
      delay: 0.2
    }} className="text-lg sm:text-2xl text-gray-700 font-medium mb-6 sm:mb-10 max-w-3xl mx-auto font-['DM_Sans',_sans-serif]" data-id="element-177">
        No es falta de voluntad. No es pereza. Es química cerebral.
        <br data-id="element-178" />
        <span className="text-gold-600 font-bold" data-id="element-179">Y tiene solución.</span>
      </motion.h2>

      {/* Product Image - moved up like reference */}
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.8,
      delay: 0.4
    }} className="relative cursor-pointer group mb-8 sm:mb-12" onClick={scrollToCheckout} data-id="element-180">
        <div className="absolute inset-0 bg-gold-500/20 blur-3xl rounded-full transform -translate-y-10" data-id="element-181"></div>
        <div className="relative w-full max-w-sm sm:max-w-md mx-auto aspect-[4/3] bg-gradient-to-br from-dark to-gray-800 rounded-2xl shadow-2xl overflow-hidden border border-gray-700 flex flex-col items-center justify-center p-6 sm:p-8 animate-float group-hover:scale-[1.02] transition-transform duration-300" data-id="element-182">
          <div className="text-gold-400 font-serif text-2xl sm:text-4xl font-bold mb-2 text-center" data-id="element-183">
            RESET DE DOPAMINA
          </div>
          <div className="text-white/80 text-xs sm:text-base tracking-widest uppercase mb-6 sm:mb-8 text-center leading-relaxed" data-id="element-184">
            Reconfigura tu cerebro para concentrarte y terminar lo que empezás
          </div>
          <div className="flex -space-x-4" data-id="element-185">
            {[1, 2, 3].map(i => <div key={i} className={`w-16 h-22 sm:w-28 sm:h-40 bg-white rounded-lg shadow-xl border border-gray-200 transform ${i === 1 ? '-rotate-12' : i === 2 ? 'z-10 -translate-y-3' : 'rotate-12'} flex items-center justify-center p-1.5 sm:p-2`} data-id="element-186">
                <div className="w-full h-full border border-gray-100 rounded flex flex-col items-center justify-center bg-gray-50" data-id="element-187">
                  <div className="w-6 sm:w-8 h-1 bg-gold-500 mb-1.5 sm:mb-2 rounded" data-id="element-188"></div>
                  <div className="w-8 sm:w-12 h-1 bg-gray-300 rounded" data-id="element-189"></div>
                </div>
              </div>)}
          </div>
          <div className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 bg-gold-500 text-dark text-[10px] sm:text-xs font-bold px-2.5 py-1 rounded-full" data-id="element-190">
            PDF BUNDLE
          </div>
        </div>
      </motion.div>

      {/* Feature rows - like reference page structure */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6,
      delay: 0.6
    }} className="max-w-2xl mx-auto mb-8 sm:mb-10 space-y-3" data-id="element-191">
        <div className="bg-dark/5 backdrop-blur-sm rounded-xl p-4 flex items-start gap-3 text-left border border-gray-200/60" data-id="element-192">
          <Brain className="text-gold-500 shrink-0 mt-0.5" size={20} data-id="element-193" />
          <p className="text-sm sm:text-base text-gray-700" data-id="element-194">
            Si tenés 50 pestañas abiertas en la cabeza y sabés lo que tenés que
            hacer pero no podés arrancar.
          </p>
        </div>
        <div className="bg-dark/5 backdrop-blur-sm rounded-xl p-4 flex items-start gap-3 text-left border border-gray-200/60" data-id="element-195">
          <Zap className="text-gold-500 shrink-0 mt-0.5" size={20} data-id="element-196" />
          <p className="text-sm sm:text-base text-gray-700" data-id="element-197">
            <span className="font-bold text-dark" data-id="element-198">Rompé el ciclo:</span> Te vas
            a dormir con una mezcla rara de agotamiento y culpa, y el desorden
            de tu mente se come el desorden de tu vida.
          </p>
        </div>
        <div className="bg-dark/5 backdrop-blur-sm rounded-xl p-4 flex items-start gap-3 text-left border border-gray-200/60" data-id="element-199">
          <Shield className="text-gold-500 shrink-0 mt-0.5" size={20} data-id="element-200" />
          <p className="text-sm sm:text-base text-gray-700 font-[700]" data-id="element-201">
            <span className="font-bold text-dark" data-id="element-202"></span> Este sistema fue
            diseñado exactamente para vos.
          </p>
        </div>
      </motion.div>

      {/* CTA Button - full width prominent like reference */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.5,
      delay: 0.8
    }} className="flex flex-col items-center max-w-lg mx-auto" data-id="element-203">
        <button onClick={scrollToCheckout} className="group relative w-full flex flex-col items-center justify-center px-6 sm:px-8 py-4 sm:py-5 text-base sm:text-lg font-bold text-white transition-all duration-200 bg-coral-500 rounded-2xl hover:bg-coral-600 hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-coral-500 shadow-lg shadow-coral-500/30" data-id="element-204">
          <span className="flex items-center gap-2" data-id="element-205">
            👉 SÍ, QUIERO MI SISTEMA AHORA
            <ChevronRight className="group-hover:translate-x-1 transition-transform" size={20} data-id="element-206" />
          </span>
          <span className="text-sm font-medium text-white/80 mt-1" data-id="element-207">
            Acceso inmediato · Descarga en segundos
          </span>
        </button>

        {/* Trust badges - like reference */}
        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs sm:text-sm text-gray-500" data-id="element-208">
          <span className="flex items-center gap-1" data-id="element-209">
            <Lock size={12} data-id="element-210" /> Pago seguro
          </span>
          <span className="text-gray-300" data-id="element-211">·</span>
          <span className="flex items-center gap-1" data-id="element-212">
            <Download size={12} data-id="element-213" /> Descarga instantánea
          </span>
          <span className="text-gray-300" data-id="element-214">·</span>
          <span className="flex items-center gap-1" data-id="element-215">
            <MapPin size={12} data-id="element-216" /> PDF descargable
          </span>
        </div>
      </motion.div>
    </section>;
};